import { useEffect } from "react";
import Lenis from "lenis";

/**
 * Global smooth-scroll driver. Mounts a single Lenis instance and rAF loop.
 * Exposes the instance as `window.__lenis__` so panels (Sheet, Dialog…) can
 * `lenis.stop()` when they open to keep the background frozen. Skips touch
 * devices to preserve native inertia on mobile.
 */
export const useLenis = () => {
  useEffect(() => {
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    if (isTouch) return;

    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1.05,
    });
    window.__lenis__ = lenis;

    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    // Ensure smooth anchor scroll from any [href^="#"] link
    const onAnchor = (e) => {
      const a = e.target.closest?.('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute("href").slice(1);
      const el = id && document.getElementById(id);
      if (el) {
        e.preventDefault();
        lenis.scrollTo(el, { offset: -40 });
      }
    };
    document.addEventListener("click", onAnchor);

    return () => {
      document.removeEventListener("click", onAnchor);
      cancelAnimationFrame(rafId);
      lenis.destroy();
      if (window.__lenis__ === lenis) window.__lenis__ = null;
    };
  }, []);
};
